/**
 * ReacceptanceModal — soft modal asking the user to re-accept ONE updated legal
 * document at a time (spec 2026-07-03-legal-consent-soft-modal, revised
 * 2026-07-04). The hard gate on grants data is gone (D1) — this modal only asks.
 *
 * Single-document design (user request 2026-07-04, replaces the old two-in-one
 * modal that stacked both documents with a checkbox each — confusing, low
 * completion):
 * - Renders exactly ONE document (the caller decides which, and in what order —
 *   LegalConsentProvider shows terms first, then privacy). Consent is sent per
 *   document (`POST /legal/accept` with a single entry), not batched.
 * - Layout: fixed header (what changed + why) → SINGLE scrollable window with
 *   ONLY the document content → fixed footer with the checkbox + "Zapisz"
 *   button. The checkbox and button never scroll out of view (user request).
 * - Closable ONLY via the "X" (aria-label="Zamknij"); backdrop click and ESC
 *   are inert (SoftModal `dismissOnBackdrop`/`dismissOnEscape` = false). Chrome
 *   (portal, backdrop, focus trap, aria-hiding, focus-return) lives in SoftModal.
 * - a11y: role="dialog" (closable, NOT alertdialog), aria-modal, aria-labelledby.
 * - Error handling: network/500 -> message + "Spróbuj ponownie" (disabled while
 *   retrying) + support fallback link. 400 LEGAL_VERSION_MISMATCH -> message +
 *   re-fetch /auth/me (accept path unchanged, ADR-085).
 * - Telemetry: logs `shown` once on mount and `dismissed` once on close.
 *   LegalConsentProvider keys this component by document type, so switching from
 *   terms to privacy remounts it -> `shown` fires per document.
 * - Brand-aware content (ADR-088, spec 2026-07-06-per-brand-legal-content
 *   AC C): `doc.brandSlug` (set by the backend on every `PendingDoc`) is
 *   passed straight through to `getDocumentHtml` — a HotelB2B user's modal
 *   renders HotelB2B's content, never KiedyNabory's.
 * - Fail-safe rendering (code review C2): `getDocumentHtml` throws
 *   fail-closed (ADR-088 C1) for an unknown/missing brand. The throw is
 *   caught and degrades to the existing empty-html fallback ("Pełną treść
 *   dokumentu otwierasz linkiem poniżej.") instead of propagating —
 *   LegalConsentProvider renders this modal as a sibling of the whole
 *   `<Router>` in App.tsx, so an uncaught throw here would unmount the
 *   entire app (white screen), not just the modal. The throw is also
 *   reported (`captureBusinessError`) since it should never happen in
 *   practice (every `brandSlug` is backend-resolved) and would indicate a
 *   real content leak risk if it ever did.
 */
import React, { useEffect, useRef, useState } from "react";
import { LEGAL_DOCUMENT_LABELS } from "@hotelb2b/legal-content";
import type {
  AcceptLegalDocument,
  LegalTelemetryAction,
  LegalTelemetryDocument,
  PendingDoc,
} from "../../services/legalApi";
import {
  acceptLegal as defaultAcceptLegal,
  postLegalTelemetry as defaultPostLegalTelemetry,
} from "../../services/legalApi";
import { getSupportEmail } from "../../utils/domainConfig";
import { isSafeHttpUrl } from "../../utils/urlSafety";
import { captureBusinessError } from "../../sentry.config";
import { getSanitizedDocumentHtml as defaultGetDocumentHtml } from "./legalDocumentContent";
import { SoftModal } from "./SoftModal";
import {
  LEGAL_VERSION_MISMATCH,
  extractLegalErrorCode,
  isNetworkError,
  mapLegalErrorCode,
} from "./legalErrorMapper";

/** Re-exported from `@hotelb2b/legal-content` (ADR-075 SoT) to avoid drift (M1). */
const DOC_LABELS: Record<PendingDoc["documentType"], string> = LEGAL_DOCUMENT_LABELS;

/**
 * Accusative Polish form for the header/description sentence
 * ("Zaktualizowaliśmy <accusative>"). The nominative `DOC_LABELS`
 * ("Regulamin" / "Polityka prywatności") reads wrong inside that sentence.
 */
const DOC_ACCUSATIVE: Record<PendingDoc["documentType"], string> = {
  terms: "regulamin",
  privacy: "politykę prywatności",
};

export interface ReacceptanceModalProps {
  /** The single document to (re-)accept in this modal. */
  doc: PendingDoc;
  /** Called after the document is accepted successfully. */
  onAccepted: () => void;
  /** Called on 400 LEGAL_VERSION_MISMATCH to re-fetch /auth/me. */
  onVersionMismatch: () => void;
  /** Called on the "X" click — hides the modal until the next view change. */
  onDismiss: () => void;
  /** Injectable for tests; defaults to the real POST /legal/accept. */
  accept?: (documents: AcceptLegalDocument[]) => Promise<void>;
  /** Injectable for tests; defaults to the real POST /legal/telemetry. */
  postTelemetry?: (
    action: LegalTelemetryAction,
    documents: LegalTelemetryDocument[],
  ) => Promise<void>;
  /**
   * Injectable for tests; defaults to the real sanitized HTML lookup
   * (`getLegalContent(brandSlug).getSanitizedHtml(docType)`, ADR-088).
   */
  getDocumentHtml?: (
    documentType: PendingDoc["documentType"],
    brandSlug: string,
  ) => string;
  /** Injectable for tests; defaults to the brand support email. */
  supportEmail?: string;
}

export const ReacceptanceModal: React.FC<ReacceptanceModalProps> = ({
  doc,
  onAccepted,
  onVersionMismatch,
  onDismiss,
  accept = defaultAcceptLegal,
  postTelemetry = defaultPostLegalTelemetry,
  getDocumentHtml = defaultGetDocumentHtml,
  supportEmail,
}) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Focus the accept checkbox on open (not the "X") — user request 2026-07-04.
  const checkboxRef = useRef<HTMLInputElement>(null);

  const resolvedSupportEmail = supportEmail ?? getSupportEmail();
  const label = DOC_LABELS[doc.documentType];
  const accusative = DOC_ACCUSATIVE[doc.documentType];
  const urlOk = isSafeHttpUrl(doc.url);
  // Fail-closed content lookup (ADR-088 C1) can throw for an unknown brand —
  // never let it escape the render and take down the whole app (C2). Falls
  // through to the existing empty-html fallback below instead.
  let html = "";
  try {
    html = getDocumentHtml(doc.documentType, doc.brandSlug);
  } catch (error) {
    captureBusinessError(
      error instanceof Error ? error : new Error(String(error)),
      { component: "ReacceptanceModal", documentType: doc.documentType, brandSlug: doc.brandSlug },
    );
  }

  const telemetryDoc: LegalTelemetryDocument = {
    documentType: doc.documentType,
    version: doc.currentVersion,
  };

  // Telemetry: log `shown` exactly once per mount. LegalConsentProvider keys
  // this component by document type, so a terms->privacy switch remounts it and
  // logs `shown` again for the new document.
  useEffect(() => {
    postTelemetry("shown", [telemetryDoc]).catch(() => {
      // Best-effort observability only — never surface a telemetry failure.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasLoggedDismiss = useRef(false);
  const handleClose = () => {
    if (!hasLoggedDismiss.current) {
      hasLoggedDismiss.current = true;
      postTelemetry("dismissed", [telemetryDoc]).catch(() => {
        // Best-effort observability only.
      });
    }
    onDismiss();
  };

  const handleSubmit = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);

    const documents: AcceptLegalDocument[] = [
      { documentType: doc.documentType, version: doc.currentVersion },
    ];

    try {
      await accept(documents);
      onAccepted();
    } catch (error) {
      const code = extractLegalErrorCode(error);
      if (code === LEGAL_VERSION_MISMATCH) {
        setErrorMessage(mapLegalErrorCode(code));
        onVersionMismatch();
        return;
      }
      if (isNetworkError(error)) {
        setErrorMessage(
          "Problem z połączeniem. Sprawdź internet i spróbuj ponownie.",
        );
      } else {
        setErrorMessage(mapLegalErrorCode(code));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SoftModal
      titleId="reacceptance-title"
      descriptionId="reacceptance-desc"
      onClose={handleClose}
      dismissOnBackdrop={false}
      dismissOnEscape={false}
      initialFocusRef={checkboxRef}
      data-testid="reacceptance-modal"
    >
      {/* Header (fixed) — what changed + why (short). */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-200">
        <h2
          id="reacceptance-title"
          className="text-lg font-semibold text-neutral-900 pr-8"
        >
          Zaktualizowaliśmy {accusative}{" "}
          <span className="text-neutral-500 font-normal text-base">
            (wersja {doc.currentVersion})
          </span>
        </h2>
        <p id="reacceptance-desc" className="text-sm text-neutral-600 mt-1">
          W związku z ostatnimi zmianami cen, nowymi funkcjami oraz zwiększeniem
          bezpieczeństwa prosimy o zapoznanie się i zaakceptowanie przed dalszym
          korzystaniem.
        </p>
      </div>

      {/* Body — SINGLE scrollable window with ONLY the document content. The
          checkbox + button live in the fixed footer below (always visible).
          Framed with a 1px border and an always-visible scrollbar
          (`legal-content-scroll`, see index.css) so the scroll affordance is
          obvious — user request 2026-07-04. */}
      <div
        tabIndex={0}
        aria-label="Treść dokumentu"
        className="legal-content-scroll flex-1 min-h-0 min-w-0 overflow-y-scroll mx-6 my-4 px-4 py-3 border border-neutral-200 rounded-md"
      >
        {html ? (
          <div
            className="text-sm text-neutral-700 prose prose-sm max-w-none break-words [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_img]:max-w-full"
            data-testid={`reacceptance-content-${doc.documentType}`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p
            className="text-sm text-neutral-600"
            data-testid={`reacceptance-content-${doc.documentType}`}
          >
            Pełną treść dokumentu otwierasz linkiem poniżej.
          </p>
        )}
      </div>

      {/* Footer (fixed) — checkbox (always visible) + submit. */}
      <div className="flex-shrink-0 px-6 pt-4 pb-6 border-t border-neutral-200 space-y-3">
        {errorMessage && (
          <div
            role="alert"
            aria-live="assertive"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
            data-testid="reacceptance-error"
          >
            <p>{errorMessage}</p>
            <p className="mt-1 text-neutral-600">
              Problem nie znika?{" "}
              <a
                href={`mailto:${resolvedSupportEmail}`}
                className="text-primary-700 hover:underline font-medium"
                data-testid="reacceptance-support-link"
              >
                Napisz do nas
              </a>
              .
            </p>
          </div>
        )}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 min-w-[24px] min-h-[24px]"
            data-testid={`reacceptance-checkbox-${doc.documentType}`}
          />
          <span className="text-sm text-neutral-700">
            Przeczytałem/am i akceptuję:{" "}
            {urlOk ? (
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-700 hover:underline font-medium"
                data-testid={`reacceptance-link-${doc.documentType}`}
              >
                {label}
                <span className="sr-only"> (otwiera się w nowej karcie)</span>
              </a>
            ) : (
              <span className="font-medium">
                {label}{" "}
                <span
                  className="text-red-600 text-xs font-normal"
                  data-testid={`reacceptance-link-unavailable-${doc.documentType}`}
                >
                  (Link niedostępny — skontaktuj się z pomocą.)
                </span>
              </span>
            )}
          </span>
        </label>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!checked || submitting}
          className="w-full px-4 py-2.5 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={errorMessage ? "reacceptance-retry" : "reacceptance-submit"}
        >
          {submitting
            ? "Zapisywanie…"
            : errorMessage
              ? "Spróbuj ponownie"
              : "Zapisz"}
        </button>
      </div>
    </SoftModal>
  );
};

export default ReacceptanceModal;
